import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import { Col, Container, Row } from 'reactstrap';

import useNavigationEntityConverter from '../../../hooks/navigation/useNavigationEntityConverter';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import AdStore from '../../../store/AdStore';
import ConfigStore from '../../../store/ConfigStore';
import CustomNavigationLinkStore from '../../../store/CustomNavigationLinkStore';
import UIStore from '../../../store/UIStore';
import AdZones from '../../../types/AdZones';
import Paths from '../../../types/Paths';
import ProductAddedToCardModalType from '../../../types/ProductAddedToCartModalStyle';
import { addParametersToPath } from '../../../util/queryString';
import ContentSlot from '../../ad/ContentSlot';
import MobileNavigationCart from '../../cart/MobileNavigationCart';
import NavigationCart from '../../cart/NavigationCart';
import NavigationCartPopover from '../../cart/NavigationCartPopover';
import NavigationHybridCart from '../../cart/NavigationHybridCart';
import NavigationEntityItemButton from '../../navigation/NavigationEntityItemButton';
import MobileNavigationProposal from '../../proposal/MobileNavigationProposal';
import NavigationProposal from '../../proposal/NavigationProposal';
import QuickSearch from '../../search/QuickSearch';
import LicensePlateInput from '../../vehicle/LicensePlateInput';
import MobileIconsNavigation from "../mobile/MobileIconsNavigation";

const CommonNavigationMiddle = ({
  accountStore,
  adStore,
  configStore,
  customNavigationLinkStore,
  uiStore,
  routeService,
  history,
  inputBlur,
  inputFocus,
  onSearchKeyPress,
  onShowSearchOnMobile,
  shouldShowSearchOnMobile,
  navigationLogo,
}) => {
  const moveShoppingCartLoginRight =
    !configStore.topNavigation.moveShoppingCartLoginRight;

  const renderNavigationEntityItemButton = (navigationLinks) => {
    const navigationEntityItems = useNavigationEntityConverter({
      entities: navigationLinks,
    });

    return navigationEntityItems.map((entity) => (
      <div key={entity.id} className="NavigationEntityButton__button-wrapper">
        <NavigationEntityItemButton
          entity={entity}
          buttonContentClassName={'NavigationEntityButton__button-content'}
        />
      </div>
    ));
  };

  const renderNavigationHeader = () => {
    const showCart = !accountStore.isViewOnly;
    const centerShopLogo = configStore.topNavigation.centerShopLogo;

    return (
      <>
        {navigationLogo}
        {!configStore.siteConfig.isHomePage && !centerShopLogo && (
          <div
            className={classNames('CommonNavigation__middle-right-content', {
              'CommonNavigation__middle-only-content': !showCart,
              'CommonNavigation__middle-center-content':
                configStore.topNavigation.center,
            })}
          >
            <div className="CommonNavigation__middle-right-content-top">
              {!configStore.topNavigation.moveSearchbarTopNavigation && (
                <>
                  <div className="CommonNavigation__middle-right-ghost" />
                  <QuickSearch
                    handleFocus={inputFocus}
                    handleBlur={inputBlur}
                    toggleSearch={onSearchKeyPress}
                  />
                </>
              )}
              {getNavigationEntityButtons()}
              {showCart &&
                moveShoppingCartLoginRight &&
                configStore.siteConfig.isWebStore && (
                  <div className="CommonNavigation__middle-right-cart-wrapper">
                    {getNavigationCart()}
                  </div>
                )}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderVehicleSearchPartHeader = () => {
    const showCart = !accountStore.isViewOnly;
    const centerShopLogo = configStore.topNavigation.centerShopLogo;

    return !centerShopLogo ? (
      <Row className="CommonNavigation__middle-content-row">
        <Col
          md={centerShopLogo ? 8 : 3}
          xl={centerShopLogo ? 8 : 4}
          className="pl-0"
        >
          {centerShopLogo && <div />}
          {navigationLogo}
        </Col>
        <Col
          md={9}
          xl={8}
          className={classNames('CommonNavigation__middle-right-content', {
            'CommonNavigation__middle-only-content': !showCart,
            'CommonNavigation__middle-center-content':
              configStore.topNavigation.center,
          })}
        >
          <div className="CommonNavigation__license-plate-input">
            {!configStore.topNavigation.moveSearchbarTopNavigation && (
              <LicensePlateInput
                handleOnSubmit={handleVehicleSearch}
                clearAfterSubmit
              />
            )}
          </div>
          <div className="CommonNavigation__middle-right-content-top">
            {!configStore.topNavigation.moveSearchbarTopNavigation && (
              <QuickSearch
                handleFocus={inputFocus}
                handleBlur={inputBlur}
                toggleSearch={onSearchKeyPress}
              />
            )}
            {getNavigationEntityButtons()}
            {showCart &&
              moveShoppingCartLoginRight &&
              configStore.siteConfig.isWebStore && (
                <div className="CommonNavigation__middle-right-cart-wrapper">
                  <NavigationCart
                    className={'NavigationCart__vehicle-search-part'}
                    wrapperClassName={
                      'NavigationCart__vehicle-search-part-cart-button-wrapper'
                    }
                  />
                  {cartIsPopover() && <NavigationCartPopover />}
                </div>
              )}
          </div>
        </Col>
      </Row>
    ) : (
      renderNavigationHeader()
    );
  };

  const desktopCommonNavigation = () => {
    const centerShopLogo = configStore.topNavigation.centerShopLogo;
    const shouldShowBanner =
      configStore.topNavigation.moveSearchbarTopNavigation;
    const vehiclePartSearchEnabled = configStore.vehiclePartSearch.enabled;
    return (
      <div
        className={classNames(
          'CommonNavigationMiddle',
          'CommonNavigation__middle-content',
          {
            'CommonNavigation__middle-content--centered-logo': centerShopLogo,
          }
        )}
      >
        {vehiclePartSearchEnabled
          ? renderVehicleSearchPartHeader()
          : renderNavigationHeader()}
        {shouldShowBanner && getBanners()}
      </div>
    );
  };

  const getNavigationEntityButtons = () => {
    const navigationLinks = customNavigationLinkStore.getNavigationEntities();

    if (!navigationLinks.length) {
      return;
    }

    return renderNavigationEntityItemButton(navigationLinks);
  };

  const getBanners = () => {
    const ads = adStore.getGlobalAdsByAdZone(AdZones.HEADER_CONTENT);

    if (!ads || ads.length === 0) {
      return null;
    }

    return (
      <div className="CommonNavigation__content-slots CommonNavigation__content-slots__container">
        {ads.map((ad) => (
          <ContentSlot
            ad={ad}
            bannerZone={AdZones.HEADER_CONTENT}
            key={ad.id}
          />
        ))}
      </div>
    );
  };

  const cartIsPopover = () => {
    if (!accountStore.account) {
      return null;
    }

    const isRetailer = accountStore.account.is_retailer;
    const modalType = configStore.cart.type;

    return isRetailer || modalType === ProductAddedToCardModalType.POPOVER;
  };

  const getNavigationCart = () => {
    const doNotRenderCart = accountStore.isViewOnly;

    if (doNotRenderCart) {
      return null;
    }

    if (configStore.siteConfig.isHybrid) {
      return (
        <NavigationHybridCart
          navigationCart={<NavigationCart isText={true} />}
          navigationProposal={<NavigationProposal isText={true} />}
        />
      );
    }

    const shouldUseSimple =
      uiStore.isMobile && configStore.cart.button.simplifiedMobile;
    const navigationCart = shouldUseSimple ? (
      <MobileNavigationCart />
    ) : (
      <NavigationCart />
    );

    const navigationProposal = shouldUseSimple ? (
      <MobileNavigationProposal />
    ) : (
      <NavigationProposal />
    );

    return (
      <div className="NavigationCart__wrapper">
        {!configStore.siteConfig.proposalEnabled && navigationCart}
        {configStore.siteConfig.proposalEnabled && navigationProposal}
        {cartIsPopover() && <NavigationCartPopover />}
      </div>
    );
  };

  const mobileCommonNavigation = () => {
    const vehiclePartSearchEnabled = configStore.vehiclePartSearch.enabled;
    const mobileSearchbarVisibility =
      configStore.topNavigation.headerMobileSearchbarVisibility;
    const visibleMobileSearchbar =
      shouldShowSearchOnMobile || mobileSearchbarVisibility;
    const hideMobileContainer = uiStore.searchIsActive;

    return (
      <div
        className={classNames(
          'CommonNavigationMiddle',
          'CommonNavigation__middle-content'
        )}
      >
        <Container className="CommonNavigation__middle-content-container">
          {!hideMobileContainer && (
            <Row
              className={classNames('CommonNavigation__mobile-container-row', {
                'CommonNavigation__mobile-container-row--hidden':
                  hideMobileContainer,
              })}
            >
              <Col xs={12} className="CommonNavigation__mobile-container">
                <MobileIconsNavigation
                  getNavigationCart={getNavigationCart}
                  onShowSearchOnMobile={onShowSearchOnMobile}
                />
              </Col>
            </Row>
          )}
          {visibleMobileSearchbar && (
            <Row
              className={classNames('CommonNavigation__mobile-search-row', {
                'CommonNavigation__mobile-search-row--no-padding':
                  hideMobileContainer,
                'CommonNavigation__mobile-search-row--show':
                  visibleMobileSearchbar,
              })}
            >
              {vehiclePartSearchEnabled && (
                <Col sm={{ size: 6, offset: 6 }} className="mb-2">
                  <LicensePlateInput
                    handleOnSubmit={handleVehicleSearch}
                    clearAfterSubmit
                  />
                </Col>
              )}
              <Col sm={12}>
                <QuickSearch
                  handleFocus={inputFocus}
                  handleBlur={inputBlur}
                  toggleSearch={onSearchKeyPress}
                />
              </Col>
            </Row>
          )}
        </Container>
      </div>
    );
  };

  const handleVehicleSearch = (licensePlate) => {
    const licensePlateUrl = licensePlate
      ? addParametersToPath(Paths.VehiclePartSearch, {
          id: licensePlate,
        })
      : Paths.VehiclePartSearch;
    const url = routeService.getPath(licensePlateUrl);

    history.push(url);
  };

  return uiStore.isDesktop
    ? desktopCommonNavigation()
    : mobileCommonNavigation();
};

CommonNavigationMiddle.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  customNavigationLinkStore: modelOf(CustomNavigationLinkStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  history: RouterPropTypes.history.isRequired,
  location: RouterPropTypes.location.isRequired,
  shouldShowSearchOnMobile: PropTypes.bool.isRequired,
  navigationLogo: PropTypes.object.isRequired,
  inputBlur: PropTypes.func.isRequired,
  inputFocus: PropTypes.func.isRequired,
  onSearchKeyPress: PropTypes.func.isRequired,
  onShowSearchOnMobile: PropTypes.func.isRequired,
};

export default inject(
  'accountStore',
  'adStore',
  'configStore',
  'customNavigationLinkStore',
  'translationStore',
  'uiStore',
  'routeService'
)(withRouter(observer(CommonNavigationMiddle)));
// observer HOC needs to be used as innermost until we have implemented MST as a hook.
//https://mobx.js.org/react-integration.html#wrap-order
